import { Children, cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { tablePanelSurfaceSx } from '@/features/shared/components/tableSystem/tablePanelSurfaceSx';
import {
  inertTablePanelTabSx,
  tablePanelTabsSx,
} from '@/features/shared/components/tableSystem/tablePanelTabs';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import { Panel } from '@/ui/composites/Panel';
import { Stack } from '@/ui/layout/Stack';
import { Box, Tab, Tabs } from '@/ui/primitives';

type DetailTablePanelProps = {
  tabLabel: string;
  tabsAriaLabel: string;
  toolbarSlot?: ReactNode;
  actionsSlot?: ReactNode;
  children: ReactNode;
};

type DetailToolbarElement = ReactElement<{
  hideDateRangeLabel?: boolean;
  showControlsWithoutSummary?: boolean;
}>;

const isTableToolbarElement = (slot: ReactNode): slot is DetailToolbarElement =>
  isValidElement(slot) && slot.type === TableToolbar;

const buildDetailToolbarSlotItem = (slot: ReactNode): ReactNode =>
  isTableToolbarElement(slot)
    ? cloneElement(slot, { hideDateRangeLabel: true, showControlsWithoutSummary: true })
    : slot;

const buildDetailToolbarSlot = (toolbarSlot: ReactNode): ReactNode[] =>
  Children.toArray(toolbarSlot).map(buildDetailToolbarSlotItem);

export const DetailTablePanel = ({
  tabLabel,
  tabsAriaLabel,
  toolbarSlot,
  actionsSlot,
  children,
}: DetailTablePanelProps): ReactElement => {
  const toolbarSlotContent = buildDetailToolbarSlot(toolbarSlot);
  const actionsSlotContent = Children.toArray(actionsSlot);

  return (
    <Panel
      padding="md"
      gap="none"
      contentGap="md"
      headerGap="none"
      headerSlot={
        <Box
          sx={{
            width: '100%',
            minWidth: 0,
            backgroundColor: 'background.paper',
            borderBottom: 'none',
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            position: 'relative',
            '&::after': buildTableDividerPseudoSx('bottom'),
          }}
        >
          <Stack
            direction="row"
            align="flex-end"
            justify="space-between"
            gap="sm"
            sx={{
              minWidth: 0,
              minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Tabs value={tabLabel} aria-label={tabsAriaLabel} sx={tablePanelTabsSx}>
                <Tab label={tabLabel} value={tabLabel} disableRipple sx={inertTablePanelTabSx} />
              </Tabs>
            </Box>
            {actionsSlotContent.length > 0 || toolbarSlotContent.length > 0 ? (
              <Stack direction="row" alignItems="center" gap="md" sx={{ flex: '0 0 auto' }}>
                {actionsSlotContent}
                {toolbarSlotContent}
              </Stack>
            ) : null}
          </Stack>
        </Box>
      }
      sx={tablePanelSurfaceSx}
    >
      <Box sx={{ width: '100%', minWidth: 0 }}>{Children.toArray(children)}</Box>
    </Panel>
  );
};
